import { useEffect, useRef, useState, useCallback } from "react";
import { UploadCloud, FileText, FileImage, Loader2, Trash2, RefreshCw, Download, Sparkles, Check, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = ".pdf,.doc,.docx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp";
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const uid = () => Math.random().toString(36).slice(2, 12);

// Upload menus/price lists → OperaMind extracts structured services for review → merge into the list.
// `onAdd(services)` is called when the customer approves reviewed rows.
export function ServiceDocumentUpload({ onAdd }) {
  const [files, setFiles] = useState([]);
  const [limits, setLimits] = useState({ max_files: 30, max_file_mb: 30 });
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [review, setReview] = useState([]); // proposed services pending approval
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceId = useRef(null);

  const loadFiles = useCallback(async () => {
    try {
      const r = await api.get("/knowledge");
      setFiles(r.data.files || []);
      if (r.data.limits) setLimits(r.data.limits);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    if (files.length + arr.length > limits.max_files) { toast.error(`You can upload up to ${limits.max_files} files.`); return; }
    setExtracting(true);
    let found = 0;
    for (const file of arr) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const r = await api.post("/services/extract", fd);
        const svcs = (r.data.services || []).map((s) => ({ ...s, _include: true, _rk: uid() }));
        found += svcs.length;
        setReview((prev) => [...prev, ...svcs]);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Could not process ${file.name}`);
      }
    }
    await loadFiles();
    setExtracting(false);
    toast[found ? "success" : "info"](found ? `Found ${found} service${found === 1 ? "" : "s"} — review below before adding.` : "No services detected — the file was still added to your AI knowledge.");
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };
  const setReviewField = (rk, patch) => setReview((prev) => prev.map((s) => (s._rk === rk ? { ...s, ...patch } : s)));
  const removeReview = (rk) => setReview((prev) => prev.filter((s) => s._rk !== rk));

  const approve = () => {
    const chosen = review.filter((s) => s._include && s.name.trim())
      .map(({ _include, _rk, ...s }) => ({ ...s, id: s.id || uid(), enabled: true }));
    if (!chosen.length) { toast.error("Select at least one service to add."); return; }
    onAdd?.(chosen);
    setReview([]);
    toast.success(`${chosen.length} service${chosen.length === 1 ? "" : "s"} added — edit or save them above.`);
  };

  const removeFile = async (id) => {
    try { await api.delete(`/knowledge/files/${id}`); await loadFiles(); toast.success("File removed"); }
    catch { toast.error("Could not remove file"); }
  };
  const startReplace = (id) => { replaceId.current = id; replaceRef.current?.click(); };
  const doReplace = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !replaceId.current) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/knowledge/files/${replaceId.current}/replace`, fd); await loadFiles(); toast.success("File replaced"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not replace file"); }
    finally { replaceId.current = null; }
  };
  const download = (id) => window.open(`${API}/knowledge/files/${id}/download?auth=${getToken()}`, "_blank");

  return (
    <div data-testid="service-doc-upload">
      <div
        data-testid="svc-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !extracting && inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-7 text-center transition-colors ${dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"}`}
      >
        <div className="mx-auto h-11 w-11 rounded-full bg-blue-600 flex items-center justify-center text-white">
          {extracting ? <Loader2 className="h-5 w-5 animate-spin" /> : <UploadCloud className="h-5 w-5" />}
        </div>
        <p className="mt-3 font-semibold text-slate-900">{extracting ? "Reading your document…" : "Upload a menu, price list, brochure or catalogue"}</p>
        <p className="text-sm text-slate-500 mt-1">Drag &amp; drop or <span className="text-blue-600 font-semibold">browse</span> — OperaMind extracts your services &amp; prices automatically</p>
        <p className="text-xs text-slate-400 mt-2">PDF, Word, images, TXT, CSV · up to {limits.max_file_mb} MB each</p>
        <input ref={inputRef} data-testid="svc-file-input" type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
        <input ref={replaceRef} type="file" accept={ACCEPT} className="hidden" onChange={doReplace} />
      </div>

      {/* Review screen */}
      {review.length > 0 && (
        <div data-testid="svc-review" className="mt-4 rounded-2xl border border-blue-200 bg-blue-50/40 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-600" /> Review extracted services ({review.filter((s) => s._include).length} selected)</p>
            <div className="flex gap-2">
              <Button data-testid="svc-review-cancel" variant="ghost" size="sm" onClick={() => setReview([])} className="text-slate-500 gap-1"><X className="h-4 w-4" /> Discard</Button>
              <Button data-testid="svc-review-approve" size="sm" onClick={approve} className="bg-blue-600 hover:bg-blue-700 gap-1"><Check className="h-4 w-4" /> Add selected</Button>
            </div>
          </div>
          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
            {review.map((s, i) => (
              <div key={s._rk} data-testid={`svc-review-row-${i}`} className={`flex items-start gap-2 rounded-lg border bg-white p-2.5 ${s._include ? "border-slate-200" : "border-slate-200 opacity-50"}`}>
                <Checkbox data-testid={`svc-review-include-${i}`} checked={s._include} onCheckedChange={(v) => setReviewField(s._rk, { _include: !!v })} className="mt-2" />
                <div className="flex-1 grid sm:grid-cols-12 gap-2">
                  <Input data-testid={`svc-review-name-${i}`} value={s.name} onChange={(e) => setReviewField(s._rk, { name: e.target.value })} placeholder="Service name" className="sm:col-span-4 h-9 font-medium" />
                  <Input data-testid={`svc-review-price-${i}`} value={s.price} onChange={(e) => setReviewField(s._rk, { price: e.target.value })} placeholder="Price" className="sm:col-span-2 h-9" />
                  <Input value={s.category} onChange={(e) => setReviewField(s._rk, { category: e.target.value })} placeholder="Category" className="sm:col-span-3 h-9" />
                  <Input value={s.description} onChange={(e) => setReviewField(s._rk, { description: e.target.value })} placeholder="Description" className="sm:col-span-3 h-9" />
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeReview(s._rk)} className="text-slate-400 hover:text-red-500 h-8 w-8 shrink-0"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploaded files (view / replace / delete) */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="svc-file-list">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Uploaded documents</p>
          {files.map((f) => (
            <div key={f.id} data-testid={`svc-file-${f.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${f.is_image ? "bg-violet-50 text-violet-600" : "bg-blue-50 text-blue-600"}`}>
                {f.is_image ? <FileImage className="h-4.5 w-4.5" /> : <FileText className="h-4.5 w-4.5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 truncate">{f.filename}</p>
                <p className="text-xs text-slate-400">{fmtSize(f.size || 0)}{f.status === "failed" ? " · no text found" : ""}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" title="Download" onClick={() => download(f.id)} className="text-slate-400 hover:text-slate-700 h-8 w-8"><Download className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" title="Replace" onClick={() => startReplace(f.id)} className="text-slate-400 hover:text-blue-600 h-8 w-8"><RefreshCw className="h-4 w-4" /></Button>
                <Button data-testid={`svc-file-delete-${f.id}`} variant="ghost" size="icon" title="Delete" onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-500 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ServiceDocumentUpload;
